import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  ReferenceLine, CartesianGrid, Legend,
} from "recharts";
import { Sparkles, Wand2, CheckCircle2, RefreshCw, Settings2, AlertTriangle, Clock, CalendarDays, Layers, Factory, ArrowRight } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

const ORANGE = "#f97316";
const PALETTE = ["#f97316", "#38bdf8", "#a78bfa", "#34d399", "#f43f5e", "#eab308", "#fb7185", "#22d3ee", "#c084fc", "#4ade80"];
const nf = (v) => (v ?? 0).toLocaleString("de-DE", { maximumFractionDigits: 1 });
const fmtStamp = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString("de-DE", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return null; }
};

const PRIO = {
  hoch: { label: "HOCH", cls: "bg-red-500/15 text-red-400 border-red-500/30" },
  mittel: { label: "MITTEL", cls: "bg-amber-500/15 text-amber-400 border-amber-500/30" },
  niedrig: { label: "NIEDRIG", cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" },
};

const Card = ({ children, className = "", delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4, delay, ease: "easeOut" }}
    className={`rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur p-5 ${className}`}
  >
    {children}
  </motion.div>
);

export default function AIProductionOptimizer() {
  const [cap, setCap] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState(false);
  const [view, setView] = useState("optimized"); // optimized | baseline
  const [showSettings, setShowSettings] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [cockpit, setCockpit] = useState(null);
  const [chatMsgs, setChatMsgs] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatBusy, setChatBusy] = useState(false);
  const [chatSession] = useState(() => "cockpit-" + Math.random().toString(36).slice(2));
  const [autoAnalyze, setAutoAnalyze] = useState(() => {
    try { return localStorage.getItem("popcrn_cockpit_auto") !== "0"; } catch { return true; }
  });

  useEffect(() => {
    api.get("/production/capacity").then(({ data }) => setCap(data)).catch(() => {});
    api.get("/production/cockpit").then(({ data }) => setCockpit(data)).catch(() => {});
    // Letztes Ergebnis sofort anzeigen …
    api.get("/production/optimize/last").then(({ data }) => {
      if (data?.has_result) { setResult(data); setSavedAt(data.saved_at || null); }
    }).catch(() => {});
    // … und beim Öffnen automatisch frisch analysieren (nur wenn Auto-KI aktiv)
    if (autoAnalyze) optimize(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const optimize = async (auto = false) => {
    setLoading(true); setApplied(false);
    try {
      const { data } = await api.post("/production/optimize");
      if (data.empty) {
        // Leeres Ergebnis darf einen vorhandenen Plan (Snapshot) nicht überschreiben,
        // besonders bei der automatischen Analyse beim Öffnen.
        if (auto) {
          setResult((prev) => (prev && !prev.empty ? prev : data));
        } else {
          setResult(data);
          setSavedAt(data.saved_at || new Date().toISOString());
          toast.info("Kein neuer Produktionsbedarf — bereits gedeckt.");
        }
      } else {
        setResult(data);
        setSavedAt(data.saved_at || new Date().toISOString());
        if (!auto) toast.success(data.ai_used ? "KI-Optimierung berechnet" : "Plan berechnet (heuristisch)");
      }
    } catch (e) {
      if (!auto) toast.error("Optimierung konnte gerade nicht berechnet werden. Bitte erneut versuchen.");
    } finally { setLoading(false); }
  };

  const applyPlan = async () => {
    if (!result?.plan?.length || applied) return;
    setApplying(true);
    try {
      const firstDate = {};
      (result.optimized?.segments || []).forEach((s) => {
        if (s.item_id && !firstDate[s.item_id]) firstDate[s.item_id] = s.date;
      });
      const items = result.plan.map((p) => ({
        recipe_id: p.recipe_id, planned_quantity: p.planned_quantity,
        planned_date: firstDate[p.item_id] || null,
      }));
      const { data } = await api.post("/production/apply-plan", { items });
      setApplied(true);
      toast.success(`${data.created} Produktionsaufträge übernommen`);
    } catch (e) {
      toast.error("Plan konnte nicht übernommen werden. Bitte erneut versuchen.");
    } finally { setApplying(false); }
  };

  const saveCap = async () => {
    try {
      const { data } = await api.post("/production/capacity", cap);
      setCap(data); toast.success("Kapazität gespeichert");
      if (result && !result.empty) optimize();
    } catch { toast.error("Speichern fehlgeschlagen"); }
  };

  const groups = result?.groups || [];
  const groupColor = useMemo(() => {
    const m = {}; groups.forEach((g, i) => { m[g] = PALETTE[i % PALETTE.length]; }); return m;
  }, [groups]);

  const sched = result && !result.empty ? (view === "optimized" ? result.optimized : result.baseline) : null;
  const utilData = (sched?.days || []).map((d) => ({ label: d.label, util: d.util_pct, over: d.util_pct > 100 }));
  const cmpData = result && !result.empty ? [
    { name: "Rüstzeit (min)", Vorher: result.baseline.metrics.setup_minutes, Nachher: result.optimized.metrics.setup_minutes },
    { name: "Dauer (Tage)", Vorher: result.baseline.metrics.makespan_days, Nachher: result.optimized.metrics.makespan_days },
    { name: "Verspätet", Vorher: result.baseline.metrics.late_count, Nachher: result.optimized.metrics.late_count },
  ] : [];

  // Gantt: segments grouped by day
  const dayList = sched?.days || [];
  const segByDay = useMemo(() => {
    const m = {}; (sched?.segments || []).forEach((s) => { (m[s.date] = m[s.date] || []).push(s); }); return m;
  }, [sched]);
  const H = sched?.days?.[0]?.capacity_hours || (cap?.hours_per_day) || 8;
  const todayISO = new Date().toISOString().slice(0, 10);

  const askAi = async (qOverride) => {
    const question = (typeof qOverride === "string" ? qOverride : chatInput).trim();
    if (!question || chatBusy) return;
    setChatMsgs((m) => [...m, { role: "user", text: question }]);
    setChatInput("");
    setChatBusy(true);
    try {
      const { data } = await api.post("/production/ai-chat", { question, session_id: chatSession });
      setChatMsgs((m) => [...m, { role: "ai", text: data.answer }]);
    } catch {
      setChatMsgs((m) => [...m, { role: "ai", text: "Die KI konnte gerade nicht antworten. Bitte erneut versuchen." }]);
    } finally { setChatBusy(false); }
  };

  return (
    <div data-testid="page-ai-optimizer" className="-m-4 md:-m-10 min-h-screen bg-[#0b0d12] text-white p-4 md:p-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest text-orange-400/90 font-bold">
            <Sparkles size={14} /> KI-Produktionsoptimierung
          </div>
          <h1 className="font-display font-black text-3xl sm:text-4xl mt-1">Produktions-Cockpit</h1>
          <p className="text-sm text-white/50 mt-1">Live-Lage, KI-Analyse &amp; optimale Produktionsreihenfolge auf einen Blick.</p>
          {savedAt && (
            <p data-testid="optimizer-saved-at" className="text-xs text-white/40 mt-1 inline-flex items-center gap-1.5">
              <Clock size={12} className="text-orange-400/80" /> Stand: {fmtStamp(savedAt)}
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button data-testid="btn-toggle-auto"
            onClick={() => { const v = !autoAnalyze; setAutoAnalyze(v); try { localStorage.setItem("popcrn_cockpit_auto", v ? "1" : "0"); } catch {} }}
            title="KI-Analyse automatisch beim Öffnen ausführen (spart Budget, wenn aus)"
            className={`inline-flex items-center gap-1.5 rounded-full border px-4 h-10 text-sm font-semibold transition-colors ${autoAnalyze ? "border-orange-500/50 text-orange-300 bg-orange-500/10" : "border-white/15 text-white/60 hover:bg-white/5"}`}>
            <Sparkles size={16} /> Auto-KI: {autoAnalyze ? "An" : "Aus"}
          </button>
          <button data-testid="btn-toggle-capacity" onClick={() => setShowSettings((s) => !s)}
            className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-4 h-10 text-sm font-semibold hover:bg-white/5">
            <Settings2 size={16} /> Kapazität
          </button>
          <button data-testid="btn-optimize" onClick={optimize} disabled={loading}
            className="inline-flex items-center gap-2 rounded-full bg-orange-500 text-black px-5 h-10 text-sm font-bold hover:bg-orange-400 transition-colors disabled:opacity-60">
            {loading ? <RefreshCw size={16} className="animate-spin" /> : <Wand2 size={16} />}
            {loading ? "Berechne…" : (result && !result.empty ? "Neu berechnen" : "Plan optimieren")}
          </button>
          {result && !result.empty && (
            <button data-testid="btn-apply-plan" onClick={applyPlan} disabled={applying || applied}
              className="inline-flex items-center gap-2 rounded-full border border-orange-500/50 text-orange-300 px-5 h-10 text-sm font-bold hover:bg-orange-500/10 disabled:opacity-60">
              <CheckCircle2 size={16} /> {applied ? "Übernommen ✓" : "Plan übernehmen"}
            </button>
          )}
        </div>
      </div>

      {/* Cockpit-Kennzahlen (immer sichtbar, dynamisch) */}
      {cockpit && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="cockpit-kpis">
          <Card data-testid="cockpit-open-orders">
            <div className="flex items-center gap-2 text-white/50 text-xs uppercase tracking-wide"><Layers size={14} className="text-orange-400" /> Offene Aufträge</div>
            <div className="font-mono font-black text-2xl mt-2">{cockpit.open_orders}</div>
            <div className="text-[11px] text-white/40 mt-1">in Planung / Produktion</div>
          </Card>
          <Card data-testid="cockpit-utilization">
            <div className="flex items-center gap-2 text-white/50 text-xs uppercase tracking-wide"><Clock size={14} className="text-orange-400" /> Auslastung heute</div>
            <div className={`font-mono font-black text-2xl mt-2 ${cockpit.utilization_today > 100 ? "text-red-400" : "text-white"}`}>{nf(cockpit.utilization_today)}%</div>
            <div className="text-[11px] text-white/40 mt-1">{nf(cockpit.used_hours_today)} / {nf(cockpit.capacity_hours)} h{cockpit.utilization_today > 100 ? " · überbucht" : ""}</div>
          </Card>
          <Card data-testid="cockpit-critical-materials">
            <div className="flex items-center gap-2 text-white/50 text-xs uppercase tracking-wide"><AlertTriangle size={14} className="text-orange-400" /> Kritische Rohstoffe</div>
            <div className={`font-mono font-black text-2xl mt-2 ${cockpit.critical_materials > 0 ? "text-amber-400" : "text-white"}`}>{cockpit.critical_materials}</div>
            <div className="text-[11px] text-white/40 mt-1 truncate">{cockpit.critical_material_names?.length ? cockpit.critical_material_names.slice(0, 2).join(", ") : "alles im grünen Bereich"}</div>
          </Card>
          <Card data-testid="cockpit-next-deliveries">
            <div className="flex items-center gap-2 text-white/50 text-xs uppercase tracking-wide"><CalendarDays size={14} className="text-orange-400" /> Nächste Liefertermine</div>
            <div className="font-mono font-black text-2xl mt-2">{cockpit.next_delivery_count}</div>
            <div className="text-[11px] text-white/40 mt-1 truncate">{cockpit.next_deliveries?.length ? `nächster: ${cockpit.next_deliveries[0].date} (${cockpit.next_deliveries[0].order_number})` : "in 7 Tagen · keine"}</div>
          </Card>
        </div>
      )}

      {/* Capacity settings */}
      {showSettings && cap && (
        <Card className="mb-6" data-testid="capacity-panel">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <label className="text-sm">Arbeitsstunden / Tag
              <input type="number" data-testid="cap-hours" value={cap.hours_per_day}
                onChange={(e) => setCap({ ...cap, hours_per_day: parseFloat(e.target.value) || 0 })}
                className="mt-1 w-full rounded-lg bg-white/5 border border-white/15 px-3 h-10 text-white" /></label>
            <label className="text-sm">Rüstzeit bei Sortenwechsel (min)
              <input type="number" data-testid="cap-setup" value={cap.setup_minutes}
                onChange={(e) => setCap({ ...cap, setup_minutes: parseFloat(e.target.value) || 0 })}
                className="mt-1 w-full rounded-lg bg-white/5 border border-white/15 px-3 h-10 text-white" /></label>
            <label className="text-sm">Standard-Durchsatz (Einh./h)
              <input type="number" data-testid="cap-throughput" value={cap.default_throughput}
                onChange={(e) => setCap({ ...cap, default_throughput: parseFloat(e.target.value) || 0 })}
                className="mt-1 w-full rounded-lg bg-white/5 border border-white/15 px-3 h-10 text-white" /></label>
          </div>
          {groups.length > 0 && (
            <div className="mt-4">
              <div className="text-xs uppercase tracking-wide text-white/40 mb-2">Durchsatz je Sorte (Einheiten/Stunde)</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {groups.map((g) => (
                  <label key={g} className="text-xs" style={{ color: groupColor[g] }}>{g}
                    <input type="number" value={(cap.throughput || {})[g] || ""}
                      placeholder={String(cap.default_throughput)}
                      onChange={(e) => setCap({ ...cap, throughput: { ...(cap.throughput || {}), [g]: parseFloat(e.target.value) || 0 } })}
                      className="mt-1 w-full rounded-lg bg-white/5 border border-white/15 px-2 h-9 text-white" /></label>
                ))}
              </div>
            </div>
          )}
          <div className="mt-4"><button data-testid="btn-save-capacity" onClick={saveCap}
            className="rounded-full bg-white/10 hover:bg-white/20 px-4 h-9 text-sm font-semibold">Speichern &amp; neu berechnen</button></div>
        </Card>
      )}

      {/* Empty / initial */}
      {!result && (
        <Card><div className="py-16 text-center text-white/50" data-testid="optimizer-idle">
          <Sparkles size={40} className="mx-auto mb-3 text-orange-400/70" />
          Klicke auf <span className="text-orange-300 font-semibold">„Plan optimieren"</span>, um aus dem offenen Bedarf einen optimalen Produktionsplan zu berechnen.
        </div></Card>
      )}
      {result?.empty && (
        <Card>
          {(result.covered || []).length > 0 ? (
            <div data-testid="optimizer-empty-covered" className="py-6">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 shrink-0 rounded-xl bg-emerald-500/15 text-emerald-400 flex items-center justify-center">
                  <CheckCircle2 size={20} />
                </div>
                <div>
                  <h3 className="font-display font-bold text-lg">Bedarf bereits gedeckt</h3>
                  <p className="text-sm text-white/50 mt-0.5">
                    Kein <b>neuer</b> Produktionsbedarf — der offene Bedarf ist durch {result.covered.length} geplante{result.covered.length === 1 ? "n" : ""} Produktionsauftrag{result.covered.length === 1 ? "" : "e"} abgedeckt.
                  </p>
                </div>
              </div>
              <div className="mt-4 space-y-2">
                {result.covered.map((c, i) => (
                  <div key={i} data-testid={`covered-${i}`} className="flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/[0.02] p-3">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold truncate">{c.item_name}</div>
                      <div className="text-[11px] text-white/50 font-mono">
                        {c.number}{c.planned_date ? ` · ${c.planned_date}` : ""} · {c.status === "in_progress" ? "in Produktion" : "geplant"}
                      </div>
                    </div>
                    <div className="font-mono font-bold text-sm shrink-0">{nf(c.quantity)} {c.unit || ""}</div>
                  </div>
                ))}
              </div>
              <Link to="/produktion" data-testid="covered-link-production"
                    className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-orange-500 text-black px-5 h-10 text-sm font-bold hover:bg-orange-400 transition-colors">
                <Factory size={16} /> Zur Produktion <ArrowRight size={16} />
              </Link>
            </div>
          ) : (
            <div className="py-16 text-center text-white/50" data-testid="optimizer-empty">
              <CheckCircle2 size={40} className="mx-auto mb-3 text-emerald-400" />
              Kein offener Produktionsbedarf — der Bestand deckt alle Bestellungen &amp; Mindestbestände.
            </div>
          )}
        </Card>
      )}

      {result && !result.empty && sched && (
        <div className="space-y-6">
          {/* KPI row */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: Layers, label: "Aufträge", val: sched.metrics.job_count, sub: `${groups.length} Sorten` },
              { icon: Clock, label: "Rüstzeit", val: `${sched.metrics.setup_minutes} min`, sub: view === "optimized" ? `Vorher ${result.baseline.metrics.setup_minutes} min` : " " },
              { icon: CalendarDays, label: "Dauer", val: `${sched.metrics.makespan_days} Tage`, sub: `${nf(sched.metrics.makespan_h)} h` },
              { icon: AlertTriangle, label: "Verspätet", val: sched.metrics.late_count, sub: sched.metrics.late_count ? "Liefertermin!" : "im Plan", danger: sched.metrics.late_count > 0 },
            ].map((k, i) => (
              <Card key={k.label} delay={i * 0.05}>
                <div className="flex items-center gap-2 text-white/50 text-xs uppercase tracking-wide">
                  <k.icon size={14} className="text-orange-400" /> {k.label}
                </div>
                <div className={`font-mono font-black text-2xl mt-2 ${k.danger ? "text-red-400" : "text-white"}`} data-testid={`kpi-${k.label}`}>{k.val}</div>
                <div className="text-[11px] text-white/40 mt-1">{k.sub}</div>
              </Card>
            ))}
          </div>

          {/* View toggle */}
          <div className="flex items-center gap-2">
            {[["optimized", "Optimiert"], ["baseline", "Ausgangsplan"]].map(([v, l]) => (
              <button key={v} data-testid={`view-${v}`} onClick={() => setView(v)}
                className={`rounded-full px-4 h-8 text-xs font-bold border transition-colors ${view === v ? "bg-orange-500 text-black border-orange-500" : "border-white/15 text-white/60 hover:text-white"}`}>{l}</button>
            ))}
            {result.ai_used && <span className="text-[11px] text-orange-300/80 inline-flex items-center gap-1"><Sparkles size={12} /> von KI berechnet</span>}
          </div>

          {/* Gantt */}
          <Card data-testid="gantt-chart">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-bold text-lg">Zeitstrahl (Gantt)</h3>
              <div className="flex flex-wrap gap-2">
                {groups.map((g) => (
                  <span key={g} className="inline-flex items-center gap-1.5 text-[11px] text-white/60">
                    <span className="h-2.5 w-2.5 rounded-sm" style={{ background: groupColor[g] }} /> {g}
                  </span>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              {dayList.map((d) => {
                const isToday = d.date === todayISO;
                return (
                <div key={d.date} className="flex items-center gap-3">
                  <div className={`w-20 shrink-0 text-xs font-mono ${isToday ? "text-orange-300 font-bold" : "text-white/50"}`}>
                    {d.label}{isToday && <span className="ml-1 rounded-full bg-orange-500/20 text-orange-300 px-1.5 py-0.5 text-[9px] align-middle">Heute</span>}
                  </div>
                  <div className={`relative flex-1 h-9 rounded-lg bg-white/5 overflow-hidden ${isToday ? "ring-1 ring-orange-400/50" : ""}`}>
                    {(segByDay[d.date] || []).map((s, idx) => (
                      <motion.div key={idx}
                        initial={{ width: 0 }} animate={{ width: `${(s.hours / H) * 100}%` }}
                        transition={{ duration: 0.5, delay: 0.1 }}
                        title={`${s.name} · ${nf(s.qty)} ${s.unit || ""} · ~${nf(s.hours)} h${s.due ? ` · Termin ${s.due}` : ""}${s.late ? " · VERSPÄTET" : ""}`}
                        className={`absolute top-0 h-full flex items-center px-2 text-[10px] font-semibold text-black/90 truncate ${s.late ? "ring-2 ring-red-500" : ""}`}
                        style={{ left: `${(s.offset_h / H) * 100}%`, background: s.late ? "#ef4444" : groupColor[s.group] || ORANGE }}>
                        {s.is_start ? s.name : ""}
                      </motion.div>
                    ))}
                    {d.overloaded && <div className="absolute inset-0 ring-2 ring-red-500 rounded-lg pointer-events-none" />}
                  </div>
                  <div className={`w-14 shrink-0 text-right text-xs font-mono ${d.overloaded ? "text-red-400" : "text-white/40"}`}>{d.util_pct}%</div>
                </div>
                );
              })}
            </div>
            <div className="text-[11px] text-white/40 mt-3 flex items-center gap-4">
              <span><span className="inline-block h-2.5 w-2.5 rounded-sm bg-red-500 mr-1 align-middle" />Engpass / verspätet</span>
              <span><span className="inline-block h-2.5 w-2.5 rounded-sm bg-white/10 mr-1 align-middle" />freie Kapazität</span>
            </div>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Utilization */}
            <Card data-testid="utilization-chart">
              <h3 className="font-display font-bold text-lg mb-4">Tägliche Auslastung</h3>
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={utilData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="uGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={ORANGE} stopOpacity={0.6} />
                      <stop offset="100%" stopColor={ORANGE} stopOpacity={0.05} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                  <XAxis dataKey="label" tick={{ fill: "rgba(255,255,255,0.5)", fontSize: 11 }} />
                  <YAxis tick={{ fill: "rgba(255,255,255,0.5)", fontSize: 11 }} unit="%" />
                  <Tooltip contentStyle={{ background: "#12151c", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#fff" }} />
                  <ReferenceLine y={100} stroke="#ef4444" strokeDasharray="4 4" label={{ value: "Kapazität", fill: "#ef4444", fontSize: 10, position: "insideTopRight" }} />
                  <Area type="monotone" dataKey="util" name="Auslastung" stroke={ORANGE} strokeWidth={2} fill="url(#uGrad)" isAnimationActive animationDuration={700} />
                </AreaChart>
              </ResponsiveContainer>
            </Card>

            {/* Before/After */}
            <Card data-testid="comparison-chart">
              <h3 className="font-display font-bold text-lg mb-4">Vorher / Nachher</h3>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={cmpData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                  <XAxis dataKey="name" tick={{ fill: "rgba(255,255,255,0.5)", fontSize: 11 }} />
                  <YAxis tick={{ fill: "rgba(255,255,255,0.5)", fontSize: 11 }} />
                  <Tooltip contentStyle={{ background: "#12151c", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#fff" }} cursor={{ fill: "rgba(255,255,255,0.05)" }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="Vorher" fill="rgba(255,255,255,0.25)" radius={[6, 6, 0, 0]} isAnimationActive animationDuration={700} />
                  <Bar dataKey="Nachher" fill={ORANGE} radius={[6, 6, 0, 0]} isAnimationActive animationDuration={700} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </div>

          {/* Rohstoff-Reichweite */}
          {(result.material_check || []).length > 0 && (
            <Card data-testid="material-check" className="mb-6">
              <h3 className="font-display font-bold text-lg mb-1 flex items-center gap-2">
                <Sparkles size={18} className="text-cyan-400" /> Rohstoff-Reichweite
              </h3>
              <p className="text-xs text-white/50 mb-4">Bestand ÷ Tagesverbrauch aus dem Plan. Niedrige Reichweite = rechtzeitig nachbestellen.</p>
              <div className="space-y-2">
                {result.material_check.map((m, i) => {
                  const p = PRIO[(m.priority || "mittel").toLowerCase()] || PRIO.mittel;
                  return (
                    <div key={i} data-testid={`material-${i}`}
                         className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.02] p-3">
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold truncate">{m.name}</div>
                        <div className="text-[11px] text-white/50">
                          Bestand {nf(m.stock)} {m.unit} · Verbrauch {nf(m.daily)} {m.unit}/Tag
                          {m.shortfall > 0 ? ` · Fehlmenge ${nf(m.shortfall)} ${m.unit}` : ""}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="font-mono font-black text-lg leading-none" data-testid={`material-reach-${i}`}>
                          {m.reach_days == null ? "∞" : `${nf(m.reach_days)} T`}
                        </div>
                        <span className={`inline-block mt-1 text-[10px] font-bold px-2 py-0.5 rounded-full border ${p.cls}`}>{p.label}</span>
                      </div>
                      {m.shortfall > 0 && (
                        <button data-testid={`material-reorder-${i}`}
                          onClick={async () => {
                            try {
                              const { data } = await api.post("/production/material-reorder", { item_id: m.item_id, quantity: m.shortfall });
                              toast.success(`Einkauf ${data.number}: ${data.item_name} · ${nf(data.quantity)} ${m.unit}`);
                            } catch (e) { toast.error(e.response?.data?.detail || "Fehler beim Anlegen"); }
                          }}
                          className="shrink-0 inline-flex items-center gap-1 rounded-full bg-orange-500/15 text-orange-300 border border-orange-500/30 px-3 h-8 text-xs font-bold hover:bg-orange-500/25">
                          Nachbestellen
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </Card>
          )}

          {/* Recommendations */}
          <Card data-testid="recommendations">
            <h3 className="font-display font-bold text-lg mb-4 flex items-center gap-2"><Sparkles size={18} className="text-orange-400" /> KI-Empfehlungen</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {(result.recommendations || []).map((r, i) => {
                const p = PRIO[(r.priority || "mittel").toLowerCase()] || PRIO.mittel;
                return (
                  <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
                    data-testid={`rec-${i}`} className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <div className="font-semibold text-sm">{r.title}</div>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${p.cls}`}>{p.label}</span>
                    </div>
                    <p className="text-xs text-white/60 leading-snug">{r.detail}</p>
                    <button data-testid={`rec-apply-${i}`} onClick={applyPlan} disabled={applying || applied}
                      className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-orange-500/15 text-orange-300 border border-orange-500/30 px-3 h-8 text-xs font-bold hover:bg-orange-500/25 disabled:opacity-50">
                      <CheckCircle2 size={13} /> {applied ? "Übernommen" : "Übernehmen"}
                    </button>
                  </motion.div>
                );
              })}
            </div>
          </Card>
        </div>
      )}

      {/* KI-Chat */}
      <Card className="mt-6" data-testid="ai-chat">
        <h3 className="font-display font-bold text-lg mb-1 flex items-center gap-2"><Sparkles size={18} className="text-orange-400" /> Frage die KI</h3>
        <p className="text-xs text-white/50 mb-3">z. B. „Was soll ich heute zuerst produzieren?" oder „Reicht der Bestand für Auftrag 5?"</p>
        {chatMsgs.length === 0 && (
          <div className="flex flex-wrap gap-2 mb-3" data-testid="chat-suggestions">
            {[
              "Was soll ich heute zuerst produzieren?",
              "Welche Rohstoffe werden knapp?",
              "Welche Aufträge sind wegen Liefertermin dringend?",
            ].map((s, i) => (
              <button key={i} type="button" data-testid={`chat-suggestion-${i}`}
                onClick={() => askAi(s)}
                disabled={chatBusy}
                className="rounded-full border border-white/15 bg-white/[0.03] text-white/80 px-3 h-8 text-xs font-semibold hover:bg-white/10 hover:text-white transition-colors disabled:opacity-50">
                {s}
              </button>
            ))}
          </div>
        )}
        <div className="space-y-2 max-h-72 overflow-y-auto mb-3">
          {chatMsgs.length === 0 && <div className="text-sm text-white/40">Noch keine Fragen gestellt. Stelle deine erste Frage an die Produktions-KI.</div>}
          {chatMsgs.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div data-testid={`chat-msg-${i}`} className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-orange-500 text-black" : "bg-white/[0.05] text-white/90 border border-white/10"}`}>{m.text}</div>
            </div>
          ))}
          {chatBusy && <div className="text-xs text-white/50 inline-flex items-center gap-1"><RefreshCw size={12} className="animate-spin" /> KI denkt nach…</div>}
        </div>
        <div className="flex items-center gap-2">
          <input data-testid="chat-input" value={chatInput} onChange={(e) => setChatInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); askAi(); } }}
            placeholder="Frage die KI…"
            className="flex-1 rounded-full bg-white/5 border border-white/15 px-4 h-11 text-white placeholder-white/30 focus:outline-none focus:border-orange-400" />
          <button data-testid="chat-send" onClick={askAi} disabled={chatBusy || !chatInput.trim()}
            className="inline-flex items-center gap-1.5 rounded-full bg-orange-500 text-black px-5 h-11 text-sm font-bold hover:bg-orange-400 disabled:opacity-50">
            {chatBusy ? <RefreshCw size={16} className="animate-spin" /> : <Sparkles size={16} />} Fragen
          </button>
        </div>
      </Card>
    </div>
  );
}
